import { useEffect } from "react";
import { Row, Col, Input, Button, Form, Flex, Card } from "antd";
import styled from "styled-components";
import Logo from "../assets/logo.svg";
import { useDispatch } from "react-redux";
import { initializeAuth, loginWithEmail } from "../redux/admin/adminSlice";
import { AppDispatch } from "../redux/store";
import { useAppSelector } from "../redux/utils/hook";
import { useNavigate } from "react-router-dom";
import { isInFlightPending } from "../utils/helper";
import { InFlightNames } from "../types/types";

type FieldType = {
  email: string;
  password: string;
};

const LoginAdmin = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { adminUId, inFlights } = useAppSelector((state) => state.admin);
  const isLoginLoading = isInFlightPending(
    inFlights,
    InFlightNames.requestLoginWithPasswordInflight
  );

  useEffect(() => {
    if (!adminUId) {
      dispatch(initializeAuth());
    } else {
      navigate("/admin");
    }
  }, [adminUId]);

  const onFinish = async (values: FieldType) => {
    dispatch(loginWithEmail(values));
  };

  return (
    <Container>
      <Row gutter={0} style={{ flex: 1, height: "100%" }}>
        <LeftColumn xs={24} sm={24} md={12} lg={12} xl={12}>
          <LogoImage src={Logo} alt="Logo" />
          <Heading>Welcome, Admin! Manage your dashboard effortlessly.</Heading>
        </LeftColumn>

        <RightColumn xs={24} sm={24} md={12} lg={12} xl={12}>
          <StyledCard title="Login as Admin">
            <Form
              name="login"
              layout="vertical"
              onFinish={onFinish}
              autoComplete="off"
              requiredMark={false}
            >
              <Form.Item<FieldType>
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Enter a valid email" },
                ]}
              >
                <StyledInput />
              </Form.Item>

              <Form.Item<FieldType>
                label="Password"
                name="password"
                rules={[
                  { required: true, message: "Please enter your password" },
                ]}
              >
                <StyledInput.Password />
              </Form.Item>

              <Form.Item>
                <LoginButton
                  type="primary"
                  htmlType="submit"
                  loading={isLoginLoading}
                >
                  Submit
                </LoginButton>
              </Form.Item>
            </Form>
          </StyledCard>
        </RightColumn>
      </Row>
    </Container>
  );
};

export default LoginAdmin;

const Container = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: row;
  overflow: auto;
  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
  }
`;

const LeftColumn = styled(Col)`
  background-color: #0d1117;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 60px;
  height: 100vh;
  text-align: left;
  @media (max-width: 768px) {
    height: 40vh;
    padding: 30px;
    align-items: center;
    text-align: center;
  }
`;

const RightColumn = styled(Col)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  height: 100vh;
  @media (max-width: 768px) {
    height: auto;
    padding: 30px;
  }
`;

const StyledCard = styled(Card)`
  width: 400px;
  text-align: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  @media (max-width: 480px) {
    width: 90%;
  }
`;

const LogoImage = styled.img`
  height: 40px;
  width: 120px;
  position: absolute;
  top: 20px;
  left: 20px;
  @media (max-width: 768px) {
    height: 40px;
    width: 100px;
    top: 15px;
    left: 15px;
  }
`;

const Heading = styled.h1`
  font-size: 32px;
  font-weight: bold;
  margin-top: 80px;
  line-height: 1.3;
  @media (max-width: 768px) {
    font-size: 24px;
    text-align: center;
    margin-top: 40px;
  }
`;

const StyledInput = styled(Input)`
  height: 40px;
`;

StyledInput.Password = styled(Input.Password)`
  height: 40px;
`;

const LoginButton = styled(Button)`
  width: 100%;
  height: 45px;
  font-size: 16px;
  font-weight: bold;
  margin-top: 1.3rem;
`;
